/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { Callout, InlineSnackbar, RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import type { Moment } from "moment";
import moment from "moment";
import * as React from "react";
import { formatTimeDifference } from "~/areas/projects/components/Releases/Deployments/NowOrLater/formatTimeDifference";
import { DeploymentModelType } from "~/areas/projects/components/Runbooks/RunbookRunNowLayout";
import type { DataBaseComponentState } from "~/components/DataBaseComponent/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent/DataBaseComponent";
import ExpandableFormSection from "~/components/form/Sections/ExpandableFormSection";
import Summary from "~/components/form/Sections/Summary";
import DatePicker from "./DatePicker/DatePicker";
import TimePicker from "./TimePicker/TimePicker";
import rollForward from "./rollFoward";
import styles from "./style.module.less";
interface NowOrLaterProps {
    modelType: DeploymentModelType;
    onScheduleDatesSet(scheduledAt: Moment, expiresAt: Moment): void;
}
export interface NowOrLaterState extends DataBaseComponentState {
    nowOrLater: NowOrLaterEnum;
    scheduledAt: Moment;
    expiresAt: Moment;
    scheduledDatePopupDate: Moment;
    expiryDatePopupDate: Moment;
    showSnackbar: boolean;
}
export enum NowOrLaterEnum {
    Now = "Now",
    Later = "Later"
}
function initialState(currentTime: Date): NowOrLaterState {
    const scheduledTime = rollForward(moment(currentTime), 10);
    const expiryTime = rollForward(scheduledTime, 30);
    return {
        nowOrLater: NowOrLaterEnum.Now,
        scheduledAt: moment(scheduledTime),
        expiresAt: moment(expiryTime),
        expiryDatePopupDate: null!,
        scheduledDatePopupDate: null!,
        showSnackbar: false,
    };
}
export default class NowOrLater extends DataBaseComponent<NowOrLaterProps, NowOrLaterState> {
    constructor(props: NowOrLaterProps) {
        super(props);
        this.state = initialState(new Date());
    }
    render() {
        const modelTypeLabel = this.props.modelType === DeploymentModelType.Deployment ? "deployment" : "run";
        const help = this.props.modelType === DeploymentModelType.Deployment ? "Choose when to start deployment" : "Choose when to run your runbook";
        return (<ExpandableFormSection title="When" errorKey="when" summary={this.buildSummary()} help={help}>
                <div className={styles.nowOrLaterContainer}>
                    <RadioButtonGroup value={this.state.nowOrLater} onChange={(val) => {
                this.setState({ nowOrLater: val as NowOrLaterEnum }, () => this.updateScheduleDates());
            }}>
                        <RadioButton value={NowOrLaterEnum.Now} label="Now" isDefault={true}/>
                        <RadioButton value={NowOrLaterEnum.Later} label="Later"/>
                    </RadioButtonGroup>
                    {this.state.nowOrLater === NowOrLaterEnum.Later && (<div className={styles.nowOrLaterCustomize}>
                            {this.state.expiresAt && this.state.scheduledAt && this.state.expiresAt <= this.state.scheduledAt && <Callout type={"warning"} title={"The expiry date must be after the scheduled date"}/>}

                            <h4>Scheduled start time</h4>
                            <div className={styles.dateTimeRow}>
                                <DatePicker date={this.state.scheduledAt} onDateChanged={this.handleScheduledAtChanged} minDate={new Date()}/>
                                <TimePicker time={this.state.scheduledAt} onTimeChanged={this.handleScheduledAtChanged}/>
                                {this.state.scheduledAt.format("[GMT] Z")}
                            </div>
                            <h4>Expiry time</h4>
                            <div className={styles.dateTimeRow}>
                                <DatePicker date={this.state.expiresAt} onDateChanged={(expiresAt) => this.setState({ expiresAt }, () => this.updateScheduleDates())} minDate={new Date()}/>
                                <TimePicker time={this.state.expiresAt} onTimeChanged={(expiresAt) => {
                    this.setState({ expiresAt }, () => this.updateScheduleDates());
                }}/>
                                {this.state.expiresAt.format("[GMT] Z")}
                            </div>
                            {this.state.expiresAt > this.state.scheduledAt && (<Callout type={"information"} title={`Scheduled ${modelTypeLabel}`}>
                                    The {modelTypeLabel} will start in approximately <b>{formatTimeDifference(this.state.scheduledAt, moment())} </b>
                                    and must begin within <b>{formatTimeDifference(this.state.expiresAt, this.state.scheduledAt)}</b> of the scheduled start time or it will time out.
                                </Callout>)}
                            <div className={"container"}>
                                <br />
                                <InlineSnackbar variant={"neutral"} show={this.state.showSnackbar} content={"Cannot set a time in the past"} onClose={() => this.setState({ showSnackbar: false })} autoHideDuration={2500}/>
                            </div>
                        </div>)}
                </div>
            </ExpandableFormSection>);
    }
    private handleScheduledAtChanged = (scheduledAt: Moment) => {
        this.setState((state) => {
            // adjust the expiry to keep the diff the same
            const previousDiff = moment.duration(state.expiresAt.diff(state.scheduledAt));
            const expiresAt = moment(scheduledAt).add(previousDiff);
            return {
                scheduledAt,
                expiresAt,
            };
        }, () => this.updateScheduleDates(true));
    };
    private updateScheduleDates(displayTimeValidationSnackbar?: boolean) {
        if (this.state.nowOrLater !== NowOrLaterEnum.Later) {
            this.props.onScheduleDatesSet(null!, null!);
            return;
        }
        let scheduledAt = this.state.scheduledAt;
        const now = moment().add(1, "minute"); // server will return an error error if you schedule for _now_
        if (scheduledAt.isBefore(now)) {
            if (displayTimeValidationSnackbar) {
                this.setState({ showSnackbar: true });
            }
            scheduledAt = now;
        }
        let expiresAt = this.state.expiresAt;
        const minimum = moment(scheduledAt).add(5, "minutes");
        if (expiresAt.isBefore(minimum)) {
            expiresAt = minimum;
        }
        this.setState({
            scheduledAt,
            expiresAt,
        });
        this.props.onScheduleDatesSet(scheduledAt, expiresAt);
    }
    private buildSummary() {
        const modelTypeLabel = this.props.modelType === DeploymentModelType.Deployment ? "deployment" : "run";
        return this.state.nowOrLater === NowOrLaterEnum.Now
            ? Summary.default("Now")
            : Summary.summary(<div>
                      The {modelTypeLabel} will start in approximately {formatTimeDifference(this.state.scheduledAt, moment())}
                  </div>);
    }
    static displayName = "NowOrLater";
}
